import React, { useCallback, useEffect, useState } from 'react';
import { Auth } from '@aws-amplify/auth';
import { ROUTE_AUTH } from '../../config/routes.config';
import Loader from '../Loader';
import ConfirmationForm from './ConfirmationForm';
import SignupParams from '../../types/registration/SignupParams.type';
import { useNavigate } from 'react-router-dom';

type Status = 'initial' | 'validating' | 'confirmed';
type ErrorCode =
  | 'Invalid verification code provided, please try again.'
  | 'Invalid code provided, please request a code again.';

// Allowed domains for redirect (security measure)
const ALLOWED_REDIRECT_DOMAINS = [
  'copilot.gekkovet.com',
  'copilot.dev-gekkovet.com',
  'localhost:1234', // for local development
];

// Validate redirect URL domain
const isValidRedirect = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    return ALLOWED_REDIRECT_DOMAINS.some(
      (domain) => parsedUrl.hostname === domain || parsedUrl.host === domain
    );
  } catch {
    return false;
  }
};

export const ConfirmationContainer = ({
  username: initialUsername,
  activationCode,
  redirectTo,
}: SignupParams) => {
  const [error, setError] = useState<ErrorCode | null>(null);
  const [state, setState] = useState<Status>('initial');
  const [code, setCode] = useState<string | null | undefined>(activationCode);
  const [username, setUsername] = useState<string | null | undefined>(
    initialUsername
  );

  const navigate = useNavigate();

  const confirmEmail = useCallback(async () => {
    if (!username || !code) return;
    Auth.confirmSignUp(username, code).then(
      (_result) => {
        setState('confirmed');
      },
      (error) => {
        setError(error.message);
      }
    );
  }, [code, username]);

  useEffect(() => {
    if (code && username) {
      setState('validating');
      confirmEmail();
    }
  }, [code, confirmEmail, username]);

  useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  useEffect(() => {
    if (state === 'confirmed') {
      // Handle redirect after successful confirmation
      if (redirectTo && isValidRedirect(redirectTo)) {
        console.log('Redirecting to:', redirectTo);
        window.location.href = redirectTo;
      } else {
        if (redirectTo && !isValidRedirect(redirectTo)) {
          console.warn('Invalid redirect URL detected:', redirectTo);
        }
        // Default flow (existing behavior)
        navigate(ROUTE_AUTH, { state: 'confirmed' });
      }
    }
  }, [navigate, state, redirectTo]);

  return (
    <>
      <ConfirmationForm
        setCode={setCode}
        userName={username}
        setUsername={setUsername}
        activationCode={activationCode}
      />
      <Loader showLoader={state !== 'initial'} />
    </>
  );
};
